import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, ChevronLeft, ChevronRight, CircleCheck } from "lucide-react";
import { Heading, Text } from "@/components/Typography";
import { cn } from "@/utils/cn";
import { useToast } from "@/hooks/useToast";
import { useGettingStarted } from "@/hooks/useGettingStarted";
import {
  trackGettingStarted,
  trackPhase,
  trackStep,
  trackStepCta,
} from "../gettingStartedTracking";
import { api } from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import { toStarEntries } from "@/utils/starPortfolio";
import type {
  GettingStartedPhase,
  GettingStartedTask,
  GettingStartedTaskKey,
} from "@/constants/gettingStarted";
import { setReturnStep } from "../returnPromptStore";
import { GettingStartedStep } from "./GettingStartedStep";
import { CarouselPreviewModal } from "./CarouselPreviewModal";
import { ReferralModal } from "./ReferralModal";

// Lazy: this widget is reachable from the always-mounted sidebar dock, and the
// booking modal pulls in the Nylas scheduler (~2MB). A static import here would
// fold Nylas into the main bundle for every page.
const CalibrationBookingModal = lazy(() =>
  import("./OneOnOneBookingModal").then((m) => ({ default: m.CalibrationBookingModal })),
);

/** Card surface, from the semantic theme layer in global.css. */
const CARD = "rounded-2xl border border-theme-border bg-theme-bg-surface";

/* ─────────────────────────────────────────────
 *  Progress ring
 * ───────────────────────────────────────────── */

export const ProgressRing = ({
  pct,
  size = 56,
  stroke = 5,
  showLabel = true,
  sublabel,
}: {
  pct: number;
  size?: number;
  stroke?: number;
  showLabel?: boolean;
  /** A second line under the percentage, e.g. "4/15" (Figma progress card). */
  sublabel?: string;
}) => {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-theme-border-subtle"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          stroke="currentColor"
          className="text-primary-purple transition-all duration-700"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (pct / 100) * circumference}
        />
      </svg>
      {showLabel && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Text
            as="span"
            className="font-bold leading-none tracking-[-0.3px] text-theme-text-heading"
            style={{ fontSize: Math.max(9, size * (sublabel ? 0.22 : 0.23)) }}
          >
            {pct}%
          </Text>
          {sublabel && (
            <Text
              as="span"
              className="mt-1 text-[9.5px] font-semibold leading-none tracking-[0.19px] text-theme-text-muted"
            >
              {sublabel}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
 *  Phase bar (prototype "phasebar")
 *
 *  Four tabs: a numbered node (✓ once complete), "PHASE N · CURRENT /
 *  COMPLETE / OPEN", the phase name, and one segment per step with an x/y
 *  count. The viewed phase is the highlighted tab (tinted, purple top edge).
 *  Every phase is open — click any tab to work on its steps.
 * ───────────────────────────────────────────── */

const PhaseBar = ({
  phases,
  viewPhaseId,
  currentPhaseId,
  doneOf,
  completeOf,
  isDone,
  onPick,
  onPickStep,
}: {
  phases: GettingStartedPhase[];
  viewPhaseId: number;
  /** The phase the expert is up to — announced as "current". */
  currentPhaseId: number;
  doneOf: (phase: GettingStartedPhase) => number;
  completeOf: (phase: GettingStartedPhase) => boolean;
  isDone: (key: GettingStartedTaskKey) => boolean;
  onPick: (phaseId: number) => void;
  /** A step segment under a phase: opens that exact step. */
  onPickStep: (phaseId: number, key: GettingStartedTaskKey) => void;
}) => (
  <div className="grid grid-cols-2 md:grid-cols-4">
    {phases.map((phase, i) => {
      const complete = completeOf(phase);
      const isViewed = phase.id === viewPhaseId;
      const state = complete ? "complete" : phase.id === currentPhaseId ? "current" : "open";

      return (
        // A div acting as the button, because each step segment inside it is
        // a button of its own (buttons can't nest).
        <div
          key={phase.id}
          role="button"
          tabIndex={0}
          onClick={() => onPick(phase.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onPick(phase.id);
            }
          }}
          aria-current={isViewed ? "step" : undefined}
          aria-label={`Phase ${phase.id}, ${phase.name} (${state}): ${doneOf(phase)} of ${phase.tasks.length} steps done`}
          className={cn(
            "relative flex min-w-0 cursor-pointer flex-col justify-center px-4 py-[22px] text-left transition-colors md:pl-6 md:pr-5",
            i % 2 === 1 && "border-l border-theme-border-subtle",
            i >= 2 && "border-t border-theme-border-subtle md:border-t-0",
            i === 2 && "md:border-l",
            isViewed
              ? "bg-gradient-to-b from-[color-mix(in_srgb,var(--color-primary-purple)_6%,var(--theme-bg-surface))] to-theme-bg-surface shadow-[inset_0_2px_0_0_var(--color-primary-purple)]"
              : "hover:bg-theme-bg-hover",
          )}
        >
          <div className="flex items-center gap-3 md:gap-5">
            <span
              className={cn(
                "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[13px] font-bold",
                isViewed
                  ? "bg-primary-purple text-white ring-4 ring-[color-mix(in_srgb,var(--color-primary-purple)_15%,transparent)]"
                  : complete
                    ? "bg-success-500 text-white"
                    : "border border-theme-border-strong bg-theme-bg-surface text-theme-text-muted",
              )}
            >
              {complete ? <Check size={14} strokeWidth={3} /> : phase.id}
            </span>
            <Text
              as="span"
              className={cn(
                "truncate text-[10.5px] font-bold uppercase leading-[15.75px] tracking-[0.84px]",
                isViewed ? "text-primary-purple" : "text-theme-text-muted",
              )}
            >
              Phase {phase.id} · {phase.name}
            </Text>
          </div>

          <div className="mt-3.5 flex h-1.5 items-center gap-2">
            <div className="flex gap-1">
              {phase.tasks.map((task, stepIdx) => (
                <button
                  key={task.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPickStep(phase.id, task.key);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  title={`Step ${stepIdx + 1}: ${task.title}`}
                  aria-label={`Phase ${phase.id}, step ${stepIdx + 1}: ${task.title}`}
                  // Taller than the bar it draws, so it is easy to hit.
                  className="group -my-2 py-2"
                >
                  <span
                    className={cn(
                      "block h-1.5 w-3.5 rounded-[3px] transition-transform group-hover:scale-y-150",
                      isDone(task.key)
                        ? "bg-primary-purple"
                        : "bg-theme-border-subtle group-hover:bg-theme-border-strong",
                    )}
                  />
                </button>
              ))}
            </div>
            <Text
              as="span"
              className="text-[11px] font-semibold leading-none text-theme-text-muted"
            >
              {doneOf(phase)}/{phase.tasks.length}
            </Text>
          </div>
        </div>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────
 *  The journey
 * ───────────────────────────────────────────── */

export const GettingStartedWidget = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    phases,
    currentPhase,
    openPhaseIndex,
    firstUndoneKey,
    isDone,
    isAutoDone,
    complete,
    phaseDoneCount,
    phaseComplete,
    completedCount,
    totalCount,
    overallPct,
    allComplete,
    isLoading,
  } = useGettingStarted();

  // The S.T.A.R. portfolio feeds the `stars` and `starPicker` blocks. Same
  // query key the hook uses, so this is a cache read rather than a second call.
  const { data: capabilities } = useQuery({
    queryKey: ["expert-application-capabilities"],
    queryFn: () => api.capabilities.getMine().catch(() => null),
    staleTime: 60 * 1000,
  });
  const stars = toStarEntries(capabilities?.starPortfolio);

  const [viewIdx, setViewIdx] = useState(openPhaseIndex);
  const [openTaskKey, setOpenTaskKey] = useState<GettingStartedTaskKey | null>(null);
  // Bumped whenever the dock (or any deep link) brings the user to a step, so
  // that step scrolls into view and pulses — the design's "arrive" moment.
  const [arriveKey, setArriveKey] = useState(0);
  const [modal, setModal] = useState<
    "calibration" | "carousel" | "referAchievers" | "referXos" | null
  >(null);
  const cardRef = useRef<HTMLElement>(null);

  // `openPhaseIndex` is 0 until the queries resolve, so the initial state
  // above is a placeholder. Sync once it settles, and again whenever the
  // current phase completes and the next becomes current.
  const lastOpenIdx = useRef(openPhaseIndex);
  useEffect(() => {
    if (lastOpenIdx.current !== openPhaseIndex) {
      lastOpenIdx.current = openPhaseIndex;
      setViewIdx(openPhaseIndex);
      setOpenTaskKey(null);
    }
  }, [openPhaseIndex]);

  // The sidebar dock links in as /getting-started?step=<key>. Honour it once,
  // then strip the param so a later reload or a Back does not drag the view
  // back to a step the user has since moved on from. The dock always links
  // with a step, including while this page is already open, so every click
  // on it lands here and replays the arrival.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const requested = searchParams.get("step");
    if (!requested) return;

    const phaseIdx = phases.findIndex((p) => p.tasks.some((t) => t.key === requested));
    if (phaseIdx >= 0) {
      setViewIdx(phaseIdx);
      setOpenTaskKey(requested as GettingStartedTaskKey);
      setArriveKey((k) => k + 1);
      lastOpenIdx.current = openPhaseIndex;
    }

    const next = new URLSearchParams(searchParams);
    next.delete("step");
    setSearchParams(next, { replace: true });
    // Deliberately keyed on the param alone: re-running when the phase data
    // settles would re-apply a step the user has already navigated away from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("step")]);

  // Announce finishing the current phase exactly once, on the transition.
  const announced = useRef<number | null>(null);
  useEffect(() => {
    if (isLoading || allComplete) return;
    if (announced.current === null) {
      announced.current = openPhaseIndex;
      return;
    }
    if (openPhaseIndex > announced.current) {
      const done = phases[announced.current];
      const next = phases[openPhaseIndex];
      announced.current = openPhaseIndex;
      trackPhase(done, "phase_completed");
      showToast({
        type: "success",
        header: `Phase ${done.id} complete`,
        message: `Up next: Phase ${next.id} · ${next.name}`,
        duration: 4000,
        placement: "top-right",
      });
    }
  }, [openPhaseIndex, isLoading, allComplete, phases, showToast]);

  // The last phase never "unlocks" a next one, so the effect above can't see
  // the journey finish — track that transition on its own, once.
  const wasAllComplete = useRef<boolean | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (wasAllComplete.current === false && allComplete) {
      trackPhase(phases[phases.length - 1], "phase_completed");
      trackGettingStarted("completed", { total_count: totalCount });
    }
    wasAllComplete.current = allComplete;
  }, [allComplete, isLoading, phases, totalCount]);

  const viewPhase = phases[Math.min(viewIdx, phases.length - 1)];

  const goToPhase = (phaseId: number) => {
    const idx = phases.findIndex((p) => p.id === phaseId);
    if (idx < 0) return;
    setViewIdx(idx);
    setOpenTaskKey(firstUndoneKey(phases[idx]));
    // Only when it actually changes — re-clicking the phase in view is not a
    // new view.
    if (idx !== viewIdx) trackPhase(phases[idx], "phase_viewed");
  };

  /** Previous / Next and the step dots: a specific step, possibly in another phase. */
  const goToStep = (phaseIdx: number, key: GettingStartedTaskKey) => {
    if (phaseIdx !== viewIdx) trackPhase(phases[phaseIdx], "phase_viewed");
    setViewIdx(phaseIdx);
    setOpenTaskKey(key);
    // Keep the new step's top in view when the reader paged from the bottom.
    const top = cardRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const runAction = (task: GettingStartedTask) => {
    if (!task.action) return;

    trackStepCta(task.key);

    if (task.action.kind === "modal") {
      setModal(task.action.modal);
      return;
    }

    if (task.action.kind === "external") {
      // Opening a new tab does not take the user anywhere, so there is nothing
      // to come back from. The click IS the claim for these — the platform
      // cannot see whether they actually edited their LinkedIn.
      if (task.source === "acknowledged" && !isDone(task.key)) {
        complete(task.key);
        trackStep(task.key, "step_completed", { method: "linkedin_opened" });
      }
      window.open(task.action.url, "_blank", "noopener,noreferrer");
      return;
    }

    // Otherwise an internal route does NOT tick the step. Instead, remember
    // which step sent the user away; the sidebar dock asks them on arrival
    // whether they finished, and records it then.
    setReturnStep(task.key);
    navigate(task.action.to);
  };

  const shownKey =
    openTaskKey && viewPhase.tasks.some((t) => t.key === openTaskKey)
      ? openTaskKey
      : firstUndoneKey(viewPhase);
  const stepIdx = Math.max(
    0,
    viewPhase.tasks.findIndex((t) => t.key === shownKey),
  );
  const task = viewPhase.tasks[stepIdx];
  const isFirstStep = viewIdx === 0 && stepIdx === 0;
  const isLastStep = viewIdx === phases.length - 1 && stepIdx === viewPhase.tasks.length - 1;

  const goDelta = (delta: 1 | -1) => {
    const target = stepIdx + delta;
    if (target >= 0 && target < viewPhase.tasks.length) {
      goToStep(viewIdx, viewPhase.tasks[target].key);
    } else if (target < 0 && viewIdx > 0) {
      const prev = phases[viewIdx - 1];
      goToStep(viewIdx - 1, prev.tasks[prev.tasks.length - 1].key);
    } else if (target >= viewPhase.tasks.length && viewIdx < phases.length - 1) {
      goToStep(viewIdx + 1, phases[viewIdx + 1].tasks[0].key);
    }
  };

  /** A step segment in the phase bar: open that exact step. */
  const pickStep = (phaseId: number, key: GettingStartedTaskKey) => {
    const idx = phases.findIndex((p) => p.id === phaseId);
    if (idx >= 0) goToStep(idx, key);
  };

  // Once the step in view is completed -- Mark complete, a CTA that ticks it,
  // or the platform detecting it -- move on to the next step by itself, after
  // a beat so the "Completed" state registers. Only on the transition, never
  // for a step that was already done when it was opened.
  const shownDone = !isLoading && !!task && isDone(task.key);
  const doneSeen = useRef<{ key: string | null; done: boolean }>({ key: null, done: false });
  useEffect(() => {
    const prev = doneSeen.current;
    doneSeen.current = { key: task?.key ?? null, done: shownDone };
    if (prev.key !== (task?.key ?? null) || prev.done || !shownDone || isLastStep) return;
    const timer = window.setTimeout(() => goDelta(1), 900);
    return () => window.clearTimeout(timer);
    // Keyed on the step and its done state only: goDelta is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.key, shownDone]);

  if (isLoading) {
    return <div className={cn(CARD, "h-[220px] animate-pulse")} aria-hidden />;
  }

  const phaseBar = (
    <section
      className={cn(CARD, "overflow-hidden shadow-card-subtle")}
      aria-label="Four-phase onboarding"
    >
      <PhaseBar
        phases={phases}
        viewPhaseId={viewPhase.id}
        currentPhaseId={currentPhase.id}
        doneOf={phaseDoneCount}
        completeOf={phaseComplete}
        isDone={isDone}
        onPick={goToPhase}
        onPickStep={pickStep}
      />
    </section>
  );

  // The finished journey keeps the progress hero above the phase bar.
  const header = (
    <section
      className={cn(CARD, "overflow-hidden shadow-card-subtle")}
      aria-label="Four-phase onboarding"
    >
      <div className="flex items-center gap-[13px] px-[18px] py-[18px] sm:gap-[21px] sm:px-[26px] sm:pb-5 sm:pt-6">
        <ProgressRing pct={overallPct} size={75} stroke={6} sublabel="complete" />
        <div className="min-w-0 flex-1">
          <Text
            as="p"
            className="text-[10px] font-black uppercase tracking-[0.12em] text-primary-purple"
          >
            {allComplete ? "Your founding launch · complete" : "Your founding launch"}
          </Text>
          <Heading
            as="h1"
            className="mb-[7px] mt-2 text-[21px] font-extrabold leading-[1.08] tracking-[-0.035em] text-theme-text-heading sm:text-[26px]"
          >
            {allComplete
              ? "All four phases complete"
              : `Phase ${currentPhase.id} · ${currentPhase.title}`}
          </Heading>
          <Text as="p" className="text-[12.5px] leading-[1.5] text-theme-text-secondary">
            {allComplete ? `${completedCount} of ${totalCount} steps done` : currentPhase.tagline}
          </Text>
        </div>
        <span className="ml-auto hidden whitespace-nowrap rounded-full bg-theme-bg-surface-raised px-[11px] py-2 text-[11px] font-bold text-theme-text-muted sm:inline">
          {completedCount}/{totalCount} completed
        </span>
      </div>
      <div className="border-t border-theme-border-subtle">
        <PhaseBar
          phases={phases}
          viewPhaseId={viewPhase.id}
          currentPhaseId={currentPhase.id}
          doneOf={phaseDoneCount}
          completeOf={phaseComplete}
          isDone={isDone}
          onPick={goToPhase}
          onPickStep={pickStep}
        />
      </div>
    </section>
  );

  const modals = (
    <>
      {modal === "calibration" && (
        <Suspense fallback={null}>
          <CalibrationBookingModal
            open
            onOpenChange={(open) => setModal(open ? "calibration" : null)}
          />
        </Suspense>
      )}
      <ReferralModal
        open={modal === "referAchievers" || modal === "referXos"}
        kind={modal === "referXos" ? "xo" : "achiever"}
        onOpenChange={(open) => !open && setModal(null)}
      />
      <CarouselPreviewModal
        open={modal === "carousel"}
        onOpenChange={(open) => setModal(open ? "carousel" : null)}
        onApprove={() => {
          complete("xo.publish.carousel");
          trackStep("xo.publish.carousel", "step_completed", { method: "carousel_approved" });
          setModal(null);
          showToast({ type: "success", message: "Spotlight carousel approved" });
        }}
      />
    </>
  );

  if (allComplete) {
    return (
      <div className="flex w-full max-w-[1154px] flex-col gap-6">
        {header}
        <div className={cn(CARD, "p-8 shadow-card-subtle")}>
          <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-theme-bg-success text-theme-text-success">
            <CircleCheck size={20} />
          </span>
          <Heading
            as="h2"
            className="text-[19px] font-bold leading-tight tracking-[-0.3px] text-theme-text-heading"
          >
            AchieveOS onboarding complete
          </Heading>
          <Text as="p" className="mt-2 text-[13px] leading-[1.7] text-theme-text-secondary">
            Your profile is live, your mini program is enrollable and the launch bundle has shipped.
            Everything now lives in Profile, Sessions and Programs.
          </Text>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              // Their public Expert profile in the Network Directory -- the same
              // view the Publish phase's "Preview profile" step opens.
              onClick={() => navigate("/directory?preview=me")}
              className="inline-flex items-center gap-2 rounded-[11px] bg-primary-purple px-5 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              Open Expert Profile
            </button>
            <button
              type="button"
              onClick={() => navigate("/programs")}
              className="inline-flex items-center gap-2 rounded-[11px] border border-theme-border px-5 py-2.5 text-[13px] font-semibold text-theme-text-secondary transition-colors hover:bg-theme-bg-hover"
            >
              Open Programs
            </button>
          </div>
        </div>
        {modals}
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-[1154px] flex-col gap-6">
      {phaseBar}

      <section
        ref={cardRef}
        aria-label={`Phase ${viewPhase.id} · ${viewPhase.title}`}
        className={cn(CARD, "scroll-mt-4 overflow-hidden shadow-card-subtle")}
      >
        <div className="flex flex-col gap-3 p-4">
          <div aria-live="polite">
            <GettingStartedStep
              task={task}
              index={stepIdx + 1}
              done={isDone(task.key)}
              autoDone={isAutoDone(task.key)}
              stepCount={viewPhase.tasks.length}
              arriveKey={arriveKey}
              // Complete only — a finished step cannot be un-ticked.
              onToggleDone={() => !isDone(task.key) && complete(task.key)}
              onAct={() => runAction(task)}
              stars={stars}
            />
          </div>

          <nav
            aria-label="Step navigation"
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <Text
                as="span"
                className="whitespace-nowrap text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.88px] text-theme-text-muted"
              >
                Step {stepIdx + 1} of {viewPhase.tasks.length}
              </Text>
              <div className="flex items-center gap-[5px]">
                {viewPhase.tasks.map((t, i) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => goToStep(viewIdx, t.key)}
                    aria-label={`Step ${i + 1}: ${t.title}`}
                    aria-current={i === stepIdx ? "step" : undefined}
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      i === stepIdx
                        ? "w-[22px] bg-primary-purple"
                        : isDone(t.key)
                          ? "w-1.5 bg-primary-purple opacity-40"
                          : "w-1.5 bg-theme-border-subtle hover:bg-theme-border-strong",
                    )}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {!isFirstStep && (
                <button
                  type="button"
                  onClick={() => goDelta(-1)}
                  className="inline-flex items-center gap-[5px] rounded-lg px-[11px] py-[7px] text-[12px] font-semibold leading-[18px] text-theme-text-muted transition-colors hover:text-primary-purple"
                >
                  <ChevronLeft size={13} /> Previous
                </button>
              )}
              <button
                type="button"
                onClick={() => goDelta(1)}
                disabled={isLastStep}
                className="inline-flex items-center gap-[5px] rounded-lg border border-theme-border-subtle px-[11px] py-[7px] text-[12px] font-semibold leading-[18px] text-theme-text-muted transition-colors hover:text-primary-purple disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-theme-text-muted"
              >
                {isLastStep ? (
                  <>
                    Finish <Check size={13} />
                  </>
                ) : (
                  <>
                    Next <ChevronRight size={13} />
                  </>
                )}
              </button>
            </div>
          </nav>
        </div>
      </section>

      {modals}
    </div>
  );
};

/** Kept as a named export so the "see full journey" link elsewhere still resolves. */
export const GettingStartedJourneyLink = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold text-primary-purple transition-colors hover:bg-theme-bg-hover"
  >
    See full journey <ArrowRight size={12} />
  </button>
);
